import { photoTagApi as api } from './photoTagApi';

export const addTagTypes = ['PhotoTags'] as const;

const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes
  })
  .injectEndpoints({
    endpoints: build => ({
      getPhotoTags: build.query<GetPhotoTagsApiResponse, GetPhotoTagsApiArg>({
        query: queryArg => ({
          url: `/photoTags`,
          params: {
            search: queryArg.search,
            offset: queryArg.offset,
            limit: queryArg.limit,
            sortby: queryArg.sortby,
            orderby: queryArg.orderby,
            userId: queryArg.userId,
            assetId: queryArg.assetId
          }
        }),
        providesTags: ['PhotoTags']
      }),
      getPhotoTagsById: build.query<
        GetPhotoTagsByIdApiResponse,
        GetPhotoTagsByIdApiArg
      >({
        query: queryArg => ({ url: `/photoTags/${queryArg.id}` }),
        providesTags: ['PhotoTags']
      }),
      createPhotoTags: build.mutation<
        CreatePhotoTagsApiResponse,
        CreatePhotoTagsApiArg
      >({
        query: queryArg => ({
          url: `/photoTags`,
          method: 'POST',
          body: queryArg.photoTag
        }),
        invalidatesTags: ['PhotoTags']
      }),
      updatePhotoTags: build.mutation<
        UpdatePhotoTagsApiResponse,
        UpdatePhotoTagsApiArg
      >({
        query: queryArg => ({
          url: `/photoTags/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.photoTag
        }),
        invalidatesTags: ['PhotoTags']
      }),
      deletePhotoTags: build.mutation<
        DeletePhotoTagsApiResponse,
        DeletePhotoTagsApiArg
      >({
        query: queryArg => ({
          url: `/photoTags/${queryArg.id}`,
          method: 'DELETE',
          params: { delete: queryArg.delete }
        }),
        invalidatesTags: ['PhotoTags']
      }),
      getPhotoTagRecommendations: build.query<
        GetPhotoTagRecommendationsApiResponse,
        GetPhotoTagRecommendationsApiArg
      >({
        query: queryArg => ({
          url: `/photoTags/recommendations/${queryArg.assetId}`,
          params: {
            search: queryArg.search
          }
        }),
        providesTags: ['PhotoTags']
      }),
      bulkAddPhotoTags: build.mutation<
        BulkAddPhotoTagsApiResponse,
        BulkAddPhotoTagsApiArg
      >({
        query: queryArg => ({
          url: `/photoTags/bulk-add`,
          method: 'POST',
          body: queryArg
        }),
        invalidatesTags: ['PhotoTags']
      }),
      bulkDeletePhotoTags: build.mutation<
        BulkDeletePhotoTagsApiResponse,
        BulkDeletePhotoTagsApiArg
      >({
        query: queryArg => ({
          url: `/photoTags/bulk-delete`,
          method: 'POST',
          body: { tagIds: queryArg.tagIds },
          params: { delete: queryArg.delete }
        }),
        invalidatesTags: ['PhotoTags']
      })
    }),
    overrideExisting: false
  });

export { injectedRtkApi as enhancedApi };

// Types
export type GetPhotoTagsApiResponse = {
  ok?: boolean;
  data?: PhotoTag[];
  message?: string;
};

export type GetPhotoTagsApiArg = {
  search?: string;
  offset?: number;
  limit?: number;
  sortby?: string;
  orderby?: string;
  userId?: string;
  assetId?: string;
};

export type GetPhotoTagsByIdApiResponse = {
  ok?: boolean;
  data?: PhotoTag;
  message?: string;
};

export type GetPhotoTagsByIdApiArg = {
  id: string;
};

export type CreatePhotoTagsApiResponse = {
  ok?: boolean;
  data?: PhotoTag;
  message?: string;
};

export type CreatePhotoTagsApiArg = {
  photoTag: PhotoTagCreateObject;
};

export type UpdatePhotoTagsApiResponse = {
  ok?: boolean;
  data?: PhotoTag;
  message?: string;
};

export type UpdatePhotoTagsApiArg = {
  id: string;
  photoTag: PhotoTagUpdateObject;
};

export type DeletePhotoTagsApiResponse = {
  ok?: boolean;
  data?: null;
  message?: string;
};

export type DeletePhotoTagsApiArg = {
  id: string;
  delete?: boolean;
};

export type GetPhotoTagRecommendationsApiResponse = {
  ok?: boolean;
  data?: Array<{ tagName: string; count: number }>;
  message?: string;
};

export type GetPhotoTagRecommendationsApiArg = {
  assetId: string;
  search?: string;
};

export type BulkAddPhotoTagsApiResponse = {
  ok?: boolean;
  data?: PhotoTag[];
  message?: string;
};

export type BulkAddPhotoTagsApiArg = {
  assetId: string;
  tags: string[];
};

export type BulkDeletePhotoTagsApiResponse = {
  ok?: boolean;
  data?: null;
  message?: string;
};

export type BulkDeletePhotoTagsApiArg = {
  tagIds: string[];
  delete?: boolean;
};

// Domain Types
export type PhotoTag = {
  id: string;
  assetId: string;
  subscriberId: string;
  tagName: string;
  confidence: number;
  tagType: 'LABEL' | 'OBJECT' | 'SCENE' | 'ACTIVITY';
  boundingBox?: object;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type PhotoTagCreateObject = {
  assetId: string;
  subscriberId: string;
  tagName: string;
  confidence?: number;
  tagType?: 'LABEL' | 'OBJECT' | 'SCENE' | 'ACTIVITY';
  boundingBox?: object;
};

export type PhotoTagUpdateObject = {
  tagName?: string;
  confidence?: number;
  tagType?: 'LABEL' | 'OBJECT' | 'SCENE' | 'ACTIVITY';
  boundingBox?: object;
};

export const {
  useGetPhotoTagsQuery,
  useGetPhotoTagsByIdQuery,
  useCreatePhotoTagsMutation,
  useUpdatePhotoTagsMutation,
  useDeletePhotoTagsMutation,
  useGetPhotoTagRecommendationsQuery,
  useBulkAddPhotoTagsMutation,
  useBulkDeletePhotoTagsMutation
} = injectedRtkApi;
